// The loader is in the head before Horizon's import map. Resolve the event
// module only after parsing finishes, including on a warm browser cache.
if (document.readyState === 'loading') {
  await new Promise(resolve => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
}
const { StandardEvents } = await import('@shopify/events');

// Sold-out phone action; no contact form or email subscription.
class RestockRequest extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.context = JSON.parse(this.querySelector('[data-cr-restock-context]').textContent);
    this.stock = JSON.parse(this.querySelector('[data-cr-stock]')?.textContent || '{}');
    this.variant = this.context.variant;
    // Horizon preselects a variant even without a variant query parameter.
    // Use that selection immediately, including when every variant is sold out.
    this.explicit = Boolean(this.variant);
    this.generation = 0;
    this.trigger = this.querySelector('.cr-restock__trigger');
    this.scope = this.closest('.shopify-section');
    this.purchase = this.previousElementSibling?.matches('product-form-component') ? this.previousElementSibling : null;
    const cartButton = this.purchase?.querySelector('[ref="addToCartButton"]');
    this.buttonSlot = cartButton?.closest('add-to-cart-component')?.parentElement;
    if (this.buttonSlot) {
      this.buttonSlot.classList.add('cr-restock-button-slot');
      this.trigger.className = cartButton.className + ' cr-restock__trigger';
      this.buttonSlot.append(this.trigger);
    }
    // Clicking the already-selected sold-out radio doesn't emit a change event.
    // Treat that click as an explicit choice too, without changing picker markup.
    this.scope?.addEventListener('click', event => {
      if (this.explicit || this.pending || !event.target.closest('variant-picker')) return;
      const input = event.target.closest('input') || event.target.closest('label')?.querySelector('input');
      if (input?.checked && input.dataset.variantId === String(this.variant?.id)) {
        this.explicit = true;
        this.render();
      }
    }, { signal });
    this.scope?.addEventListener(StandardEvents.productSelect, event => this.select(event), { signal });
    // Keep the ordinary form path closed while a zero-stock variant is selected.
    this.scope?.addEventListener('submit', event => {
      if (this.pending || !this.variant?.available || this.needsCall()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.trigger.focus();
      }
    }, { signal, capture: true });
    this.render();
  }

  disconnectedCallback() {
    this.setPurchaseState(false);
    if (this.buttonSlot) {
      this.buttonSlot.classList.remove('cr-restock-button-slot');
      this.append(this.trigger);
    }
    this.controller?.abort();
    this.controller = null;
    this.generation++;
  }

  async select(event) {
    if (event.target.closest('product-card, quick-add-dialog')) return;
    const generation = ++this.generation;
    this.pending = true;
    this.hidden = true;
    this.trigger.hidden = true;
    this.setPurchaseState(true);
    try {
      const { detail } = await event.promise;
      if (generation !== this.generation || !this.isConnected) return;
      // Combined listings change the product context; load the destination page so
      // requests cannot accidentally retain the previous product's name or URL.
      if (detail?.newProduct?.url && String(detail.newProduct.id) !== String(this.context.id)) {
        const destination = new URL(detail.newProduct.url, location.origin);
        if (destination.origin === location.origin) {
          if (detail.resource?.id) destination.searchParams.set('variant', detail.resource.id);
          location.assign(destination.href);
        }
        return;
      }
      if (detail?.productId && String(detail.productId) !== String(this.context.id)) return;
      this.variant = detail?.resource ?? null;
      const freshStock = detail?.html?.querySelector('[data-cr-stock]');
      if (freshStock) this.stock = JSON.parse(freshStock.textContent);
      this.explicit = true;
      this.pending = false;
      this.render();
    } catch (error) {
      // Keep stale requests hidden when variant resolution fails or is superseded.
      if (generation === this.generation && error.name !== 'AbortError') console.warn('Restock option could not be resolved.', error);
    }
  }

  needsCall() {
    const stock = this.stock?.[String(this.variant?.id)];
    return Boolean(this.variant?.available && stock?.tracked && stock?.physical && stock.quantity <= 0);
  }

  render() {
    this.hidden = !this.explicit || !this.needsCall();
    this.setPurchaseState(!this.hidden);
    this.trigger.hidden = this.hidden;
    this.scope?.classList.toggle('cr-stock-unavailable', !this.variant?.available);

  }

  setPurchaseState(active) {
    this.purchase?.classList.toggle('cr-restock-purchase-hidden', active);
    this.scope?.classList.toggle('cr-stock-call-required', active);
    const quantity = this.purchase?.querySelector('quantity-selector-component');
    if (quantity) quantity.inert = active;
  }
}

if (!customElements.get('cr-restock-request')) customElements.define('cr-restock-request', RestockRequest);

function mount() {
  const template = document.getElementById('cr-restock-template');
  if (!template || document.querySelector('cr-restock-request')) return;
  const id = template.content.querySelector('cr-restock-request')?.dataset.productId;
  const host = [...document.querySelectorAll('main product-form-component')].find(
    element => element.dataset.productId === id && !element.closest('product-card, quick-add-dialog')
  );
  if (host) host.after(template.content.cloneNode(true));
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
else mount();
document.addEventListener('shopify:section:load', mount);
